import {AfterViewInit, Component, effect, inject, NgZone, OnDestroy, PLATFORM_ID, signal} from '@angular/core';
import {ConfigurationStore} from '../../store/configuration.store';
import {PublicScheduleStore} from '../../store/public-schedule.store';
import {isPlatformBrowser} from '@angular/common';

@Component({
  selector: 'app-maps',
  templateUrl: './maps.component.html',
  styleUrl: './maps.component.scss',
  standalone: true
})
export class MapsComponent implements AfterViewInit, OnDestroy {
  private readonly configStore = inject(ConfigurationStore);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly publicScheduleStore = inject(PublicScheduleStore);
  private readonly ngZone = inject(NgZone);

  readonly name = this.configStore.name;
  readonly address = this.configStore.address;
  readonly schedule = this.publicScheduleStore.schedule;

  private map: any = null;
  mapLoaded = signal(false);


  constructor() {
    // Écoute les changements de l'adresse
    effect(() => {
      const addr = this.address();
      if (addr && addr.trim() && isPlatformBrowser(this.platformId)) {
        this.ngZone.runOutsideAngular(() => {
          this.initMapWithAddress(addr);
        })
      }
    });
  }

  async ngAfterViewInit() {
    // Ne rien faire ici → on attend le signal
  }

  private async initMapWithAddress(address: string) {
    if (this.map) return; // Carte déjà initialisée

    try {
      const leafletModule = await import('leaflet');
      const L = leafletModule.default || leafletModule;

      this.setupLeafletIcons(L);

      const coords = await this.geocodeAddress(address);

      this.map = L.map('map').setView([coords.lat, coords.lng], 15);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19
      }).addTo(this.map);

      const marker = L.marker([coords.lat, coords.lng]).addTo(this.map);
      marker.bindPopup(`
        <div style="text-align: center; min-width: 200px;">
          <strong style="color: var(--color-primary);">${this.name()}</strong><br>
          ${address}
        </div>
      `).openPopup();

      this.ngZone.run(() => {
        this.mapLoaded.set(true);
      })
    } catch (error) {
      console.error('Erreur lors de l\'initialisation de la carte:', error);
    }
  }

  private setupLeafletIcons(L: any) {
    const iconRetinaUrl = 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png';
    const iconUrl = 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png';
    const shadowUrl = 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png';

    delete (L.Icon.Default.prototype as any)._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl,
      iconUrl,
      shadowUrl,
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      tooltipAnchor: [16, -28],
      shadowSize: [41, 41]
    });
  }

  private async geocodeAddress(address: string, retries = 3): Promise<{ lat: number, lng: number }> {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`);
        const data = await response.json();

        if (data && data.length > 0) {
          return {
            lat: parseFloat(data[0].lat),
            lng: parseFloat(data[0].lon)
          };
        }
        console.warn(`Géocodage échoué pour "${address}", tentative ${attempt}...`);
      } catch (error) {
        console.error(`Erreur réseau, tentative ${attempt}:`, error);
      }
      await new Promise(res => setTimeout(res, 500));
    }
    throw new Error(`Impossible de géocoder l'adresse: "${address}".`);
  }

  formatTime(time: string): string {
    return time ? time.substring(0, 5) : '';
  }

  ngOnDestroy() {
    if (this.map) {
      this.ngZone.runOutsideAngular(() => {
        this.map.remove();
      })
      this.map = null;
    }
  }
}
